import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTenantNav } from '../hooks/useTenantNav';
import { jiraApi, JiraProject, JiraIssue, JiraProjectDetails, JiraBoard } from '../api/client';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import { ppmProjectsApi, PpmProjectListItem, portfoliosApi, PortfolioListItem } from '../api/ppm';
import { usersApi, DbUser } from '../api/client';
import { useTenant } from '../contexts/TenantContext';
import './ProjectList.css';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { PageLoader } from './PageLoader';
import { tokens } from '../theme/tokens';

interface ProjectListProps {
  onSelectProject: (projectId: string) => void;
  selectedProjectId?: string;
}

interface ProjectSummary {
  totalIssues: number;
  nextDueDate: Date | null;
  daysUntilDue: number | null;
  openIssues: number;
  doneIssues: number;
  percentComplete: number; // derived from status categories (DONE / total)
}

const ProjectList: React.FC<ProjectListProps> = ({ onSelectProject, selectedProjectId }) => {
  const { projectKey } = useParams<{ projectKey?: string }>();
  const { navigate } = useTenantNav();
  const location = useLocation();
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'projects');
  const { currentTenant } = useTenant();
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<JiraProject | null>(null);
  const [ppmProjects, setPpmProjects] = useState<PpmProjectListItem[]>([]);
  const [loadingPpm, setLoadingPpm] = useState(true);
  const [projectDetails, setProjectDetails] = useState<JiraProjectDetails | null>(null);
  const [boards, setBoards] = useState<JiraBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<JiraBoard | null>(null);
  const [boardIssues, setBoardIssues] = useState<JiraIssue[]>([]);
  const [issues, setIssues] = useState<JiraIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'summary' | 'board' | 'list' | 'timeline'>('summary');
  const [searchTerm, setSearchTerm] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage] = useState<{ variant: 'success' | 'critical'; label: string }>({ variant: 'success', label: '' });
  const [projectSummaries, setProjectSummaries] = useState<Map<string, ProjectSummary>>(new Map());
  const [loadingSummaries, setLoadingSummaries] = useState<Set<string>>(new Set());

  const [portfolios, setPortfolios] = useState<PortfolioListItem[]>([]);
  const [allUsers, setAllUsers] = useState<DbUser[]>([]);
  const [jiraPortfolioByKey, setJiraPortfolioByKey] = useState<Record<string, { portfolioId: string | null; portfolioName: string | null }>>({});
  const [createInternalOpen, setCreateInternalOpen] = useState(false);
  const [createInternal, setCreateInternal] = useState({
    name: '',
    description: '',
    portfolioId: '',
    budgetedCost: '',
    leadUserId: '',
    managerUserId: '',
    startDate: '',
    plannedEndDate: '',
  });

  const [attachJiraOpen, setAttachJiraOpen] = useState(false);
  const [attachJira, setAttachJira] = useState<{ jiraKey: string; name: string; portfolioId: string }>({ jiraKey: '', name: '', portfolioId: '' });
  const [portfolioMenuAnchor, setPortfolioMenuAnchor] = useState<null | HTMLElement>(null);
  const [portfolioMenuProject, setPortfolioMenuProject] = useState<null | { jiraKey: string; name: string }>(null);
  const [openPortfolioGroups, setOpenPortfolioGroups] = useState<Record<string, boolean>>({});
  const [openJiraPortfolioGroups, setOpenJiraPortfolioGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!currentTenant) return; // Wait for tenant to load
    fetchProjects();
    fetchPpmProjects();
    fetchPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.slug]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!canEdit) return;
        const res = await usersApi.getAll({ scopePage: 'projects' });
        if (!mounted) return;
        setAllUsers((res.data || []) as any);
      } catch {
        if (!mounted) return;
        setAllUsers([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [canEdit]);

  useEffect(() => {
    // Fetch summaries for all projects
    if (projects.length > 0) {
      projects.forEach(project => {
        fetchProjectSummary(project.key);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  useEffect(() => {
    // If projectKey is in URL, select that project
    if (projectKey && projects.length > 0) {
      const project = projects.find(p => p.key === projectKey);
      if (project) {
        setSelectedProject(project);
      }
    }
  }, [projectKey, projects]);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails(selectedProject.key);
      fetchBoards(selectedProject.key);
      fetchProjectIssues(selectedProject.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!canEdit) {
        // Non-admin users should not load global Jira project directory
        setProjects([]);
        return;
      }
      const response = await jiraApi.getProjects();
      if (response.data.success) {
        setProjects(response.data.projects || []);
      } else {
        setError(response.data.error || 'Failed to fetch projects');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPpmProjects = async () => {
    try {
      setLoadingPpm(true);
      // Only show INTERNAL projects in the PPM section. Jira projects remain in the Jira list.
      const res = await ppmProjectsApi.getAll({ source: 'internal' });
      setPpmProjects(res.data || []);
    } catch (err: any) {
      // non-fatal: Jira projects can still show
      console.error('Failed to fetch PPM projects:', err);
    } finally {
      setLoadingPpm(false);
    }
  };

  const fetchPortfolios = async () => {
    try {
      if (!canEdit) {
        // Non-admin users cannot access portfolios; keep empty.
        setPortfolios([]);
        setJiraPortfolioByKey({});
        return;
      }
      const res = await portfoliosApi.getAll();
      const ps = (res.data || []) as PortfolioListItem[];
      setPortfolios(ps);
      setCreateInternal((v) => ({ ...v, portfolioId: v.portfolioId || (ps[0]?.id || '') }));
      setAttachJira((v) => ({ ...v, portfolioId: v.portfolioId || (ps[0]?.id || '') }));

      // Load portfolio assignments for Jira projects that have been attached in PPM.
      try {
        const jiraRes = await ppmProjectsApi.getAll({ source: 'jira' });
        const map: Record<string, { portfolioId: string | null; portfolioName: string | null }> = {};
        (jiraRes.data || []).forEach((p) => {
          if (!p.jiraKey) return;
          map[String(p.jiraKey)] = {
            portfolioId: p.portfolioId ? String(p.portfolioId) : null,
            portfolioName: p.Portfolio?.name ? String(p.Portfolio.name) : null,
          };
        });
        setJiraPortfolioByKey(map);
      } catch {
        // ignore
      }
    } catch (err) {
      // non-fatal
    }
  };

  const createInternalProject = async () => {
    if (!createInternal.name.trim()) return;
    if (!createInternal.portfolioId) return;
    const budget = Number(createInternal.budgetedCost || 0);
    if (!Number.isFinite(budget) || budget <= 0) return;
    const p = portfolios.find((x) => String(x.id) === String(createInternal.portfolioId));
    const portfolioBudget = Number((p as any)?.totalBudget || 0);
    const allocated = (ppmProjects || [])
      .filter((pr: any) => String(pr.portfolioId || '') === String(createInternal.portfolioId))
      .reduce((s: number, pr: any) => s + Number(pr?.budgetedCost || 0), 0);
    const remaining = portfolioBudget - allocated;
    if (portfolioBudget > 0 && budget > remaining + 1e-9) return;
    try {
      await ppmProjectsApi.create({
        name: createInternal.name.trim(),
        description: createInternal.description.trim() ? createInternal.description.trim() : undefined,
        status: 'PLANNING',
        priority: 'MEDIUM',
        tags: ['internal'],
        portfolioId: createInternal.portfolioId,
        budgetedCost: budget,
        leadUserId: createInternal.leadUserId || undefined,
        managerUserId: createInternal.managerUserId || undefined,
        startDate: createInternal.startDate ? new Date(createInternal.startDate) : undefined,
        plannedEndDate: createInternal.plannedEndDate ? new Date(createInternal.plannedEndDate) : undefined,
      });
      setCreateInternalOpen(false);
      setCreateInternal({
        name: '',
        description: '',
        portfolioId: createInternal.portfolioId,
        budgetedCost: '',
        leadUserId: '',
        managerUserId: '',
        startDate: '',
        plannedEndDate: '',
      });
      fetchPpmProjects();
    } catch (err) {
      // ignore; existing toast system is minimal here
    }
  };

  const attachJiraProjectToPortfolio = async () => {
    if (!attachJira.jiraKey || !attachJira.portfolioId) return;
    try {
      await ppmProjectsApi.upsertJiraProject(attachJira.jiraKey, { name: attachJira.name, portfolioId: attachJira.portfolioId });
      setAttachJiraOpen(false);
      setJiraPortfolioByKey((prev) => ({
        ...prev,
        [attachJira.jiraKey]: {
          portfolioId: attachJira.portfolioId,
          portfolioName: portfolios.find((p) => p.id === attachJira.portfolioId)?.name || null,
        },
      }));
    } catch (err) {
      // ignore
    }
  };

  const setJiraPortfolio = async (jiraKey: string, name: string, portfolioId: string) => {
    try {
      await ppmProjectsApi.upsertJiraProject(jiraKey, { name, portfolioId: portfolioId || null });
      setJiraPortfolioByKey((prev) => ({
        ...prev,
        [jiraKey]: {
          portfolioId: portfolioId || null,
          portfolioName: portfolios.find((p) => p.id === portfolioId)?.name || null,
        },
      }));
    } catch (err) {
      // ignore
    }
  };

  const openPortfolioMenu = (e: React.MouseEvent<HTMLElement>, jiraKey: string, name: string) => {
    e.stopPropagation();
    setPortfolioMenuProject({ jiraKey, name });
    setPortfolioMenuAnchor(e.currentTarget);
  };

  const closePortfolioMenu = () => {
    setPortfolioMenuAnchor(null);
    setPortfolioMenuProject(null);
  };

  const fetchProjectDetails = async (projectKey: string) => {
    try {
      setLoadingDetails(true);
      const response = await jiraApi.getProject(projectKey);
      if (response.data.success) {
        setProjectDetails(response.data.project);
      }
    } catch (err: any) {
      console.error('Failed to fetch project details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchBoards = async (projectKey: string) => {
    try {
      const response = await jiraApi.getBoards(projectKey);
      if (response.data.success) {
        const fetchedBoards = response.data.boards || [];
        setBoards(fetchedBoards);
        if (fetchedBoards.length > 0 && !selectedBoard) {
          setSelectedBoard(fetchedBoards[0]);
          fetchBoardIssues(fetchedBoards[0].id);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch boards:', err);
    }
  };

  const fetchBoardIssues = async (boardId: number) => {
    try {
      setLoadingBoard(true);
      const response = await jiraApi.getBoardIssues(boardId);
      if (response.data.success) {
        setBoardIssues(response.data.issues || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch board issues:', err);
    } finally {
      setLoadingBoard(false);
    }
  };

  const fetchProjectIssues = async (projectKey: string) => {
    try {
      const jql = `project = ${projectKey} ORDER BY duedate ASC, created DESC`;
      const response = await jiraApi.searchIssues(jql, ['summary', 'status', 'assignee', 'created', 'updated', 'duedate', 'priority', 'issuetype', 'reporter']);
      if (response.data.success) {
        setIssues(response.data.issues || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch project issues:', err);
    }
  };

  const fetchProjectSummary = async (projectKey: string) => {
    if (loadingSummaries.has(projectKey)) return;
    
    try {
      setLoadingSummaries(prev => new Set(prev).add(projectKey));
      const jql = `project = ${projectKey} ORDER BY duedate ASC`;
      const response = await jiraApi.searchIssues(jql, ['status', 'duedate']);
      
      if (response.data.success) {
        const projectIssues = response.data.issues || [];
        const openIssues = projectIssues.filter(i => {
          const status = i.fields.status.name.toLowerCase();
          return !status.includes('done') && !status.includes('closed') && !status.includes('resolved');
        });

        // Use the same status bucketing logic as the Jira board view.
        const doneIssues = projectIssues.filter((i: any) => getStatusCategory(String(i?.fields?.status?.name || '')) === 'DONE').length;
        const percentComplete = projectIssues.length > 0 ? (doneIssues / projectIssues.length) * 100 : 0;
        
        // Find next due date
        const issuesWithDueDates = projectIssues
          .filter(i => i.fields.duedate)
          .map(i => new Date(i.fields.duedate!))
          .filter(date => !isNaN(date.getTime()))
          .sort((a, b) => a.getTime() - b.getTime());
        
        const nextDueDate = issuesWithDueDates.length > 0 ? issuesWithDueDates[0] : null;
        const now = new Date();
        const daysUntilDue = nextDueDate ? Math.ceil((nextDueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
        
        setProjectSummaries(prev => {
          const newMap = new Map(prev);
          newMap.set(projectKey, {
            totalIssues: projectIssues.length,
            nextDueDate,
            daysUntilDue,
            openIssues: openIssues.length,
            doneIssues,
            percentComplete,
          });
          return newMap;
        });
      }
    } catch (err: any) {
      console.error(`Failed to fetch summary for project ${projectKey}:`, err);
    } finally {
      setLoadingSummaries(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectKey);
        return newSet;
      });
    }
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('done') || statusLower.includes('closed') || statusLower.includes('resolved')) {
      return '#10B981';
    }
    if (statusLower.includes('review') || statusLower.includes('testing') || statusLower.includes('qa')) {
      return '#F59E0B';
    }
    if (statusLower.includes('progress') || statusLower.includes('in progress') || statusLower.includes('development')) {
      return '#3B82F6';
    }
    return '#6B7280';
  };

  const groupIssuesByStatus = (issues: JiraIssue[]) => {
    const grouped: { [key: string]: JiraIssue[] } = {};
    issues.forEach(issue => {
      const status = issue.fields.status.name;
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(issue);
    });
    return grouped;
  };

  const getStatusCategory = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('done') || statusLower.includes('closed') || statusLower.includes('resolved')) {
      return 'DONE';
    }
    if (statusLower.includes('review') || statusLower.includes('testing') || statusLower.includes('qa')) {
      return 'IN REVIEW';
    }
    if (statusLower.includes('progress') || statusLower.includes('in progress') || statusLower.includes('development')) {
      return 'IN PROGRESS';
    }
    return 'TO DO';
  };

  const getKanbanColumns = () => {
    const allIssues = selectedBoard ? boardIssues : issues;
    const grouped = groupIssuesByStatus(allIssues);
    
    const columns = [
      { id: 'todo', title: 'TO DO', statuses: ['TO DO', 'Open', 'Backlog'] },
      { id: 'inprogress', title: 'IN PROGRESS', statuses: ['IN PROGRESS', 'In Progress', 'Development'] },
      { id: 'inreview', title: 'IN REVIEW', statuses: ['IN REVIEW', 'In Review', 'Testing', 'QA'] },
      { id: 'done', title: 'DONE', statuses: ['DONE', 'Done', 'Closed', 'Resolved'] },
    ];

    return columns.map((column) => {
      const columnIssues: JiraIssue[] = [];
      Object.keys(grouped).forEach((status) => {
        const category = getStatusCategory(status);
        if (column.statuses.some(s => category.includes(s.toUpperCase().replace(/\s/g, '')))) {
          columnIssues.push(...grouped[status]);
        }
      });
      Object.keys(grouped).forEach((status) => {
        if (column.statuses.some(s => status.toLowerCase().includes(s.toLowerCase()))) {
          if (!columnIssues.some(i => i.id === grouped[status][0]?.id)) {
            columnIssues.push(...grouped[status]);
          }
        }
      });
      return { ...column, issues: columnIssues };
    });
  };

  const openInJira = (path: string) => {
    const baseUrl = process.env.REACT_APP_JIRA_URL || '';
    if (baseUrl) {
      window.open(`${baseUrl}${path}`, '_blank');
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const jiraProjectsByPortfolio = useMemo(() => {
    const map = new Map<string, JiraProject[]>();
    (filteredProjects || []).forEach((p) => {
      const assigned = jiraPortfolioByKey[p.key]?.portfolioName ? String(jiraPortfolioByKey[p.key]?.portfolioName) : null;
      const key = assigned || 'No portfolio yet';
      map.set(key, [...(map.get(key) || []), p]);
    });
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === 'No portfolio yet') return 1;
      if (b === 'No portfolio yet') return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({
      portfolioName: k,
      projects: (map.get(k) || []).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    }));
  }, [filteredProjects, jiraPortfolioByKey]);

  const filteredPpmProjects = ppmProjects.filter((p) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const name = (p.name || '').toLowerCase();
    const id = (p.id || '').toLowerCase();
    const jiraKey = (p.jiraKey || '').toLowerCase();
    const portfolio = (p.Portfolio?.name || '').toLowerCase();
    return name.includes(q) || id.includes(q) || jiraKey.includes(q) || portfolio.includes(q);
  });

  const ppmProjectsByPortfolio = useMemo(() => {
    const map = new Map<string, PpmProjectListItem[]>();
    (filteredPpmProjects || []).forEach((p) => {
      const key = p.Portfolio?.name ? String(p.Portfolio.name) : 'Unassigned';
      map.set(key, [...(map.get(key) || []), p]);
    });
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({
      portfolioName: k,
      projects: (map.get(k) || []).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    }));
  }, [filteredPpmProjects]);

  const summary = useMemo(() => {
    const internalTotal = ppmProjects.length;
    const jiraTotal = projects.length;
    const total = internalTotal + jiraTotal;

    const internalBudget = (ppmProjects || []).reduce((s, p: any) => s + Number(p?.budgetedCost || 0), 0);
    const internalAvgProgress =
      internalTotal > 0 ? (ppmProjects || []).reduce((s, p: any) => s + Number(p?.percentComplete || 0), 0) / internalTotal : 0;

    return { total, internalTotal, jiraTotal, internalBudget, internalAvgProgress };
  }, [ppmProjects, projects]);

  const moneyCompact = useMemo(() => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
  }, []);

  const [topPanel, setTopPanel] = useState<null | 'total' | 'internal' | 'budget'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const panelInternalRows = useMemo(() => {
    return (filteredPpmProjects || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      portfolio: p.Portfolio?.name || 'Unassigned',
      budgetedCost: Number(p.budgetedCost || 0),
      actualCost: Number(p.actualCost || 0),
      percentComplete: Number(p.percentComplete || 0),
      jiraKey: p.jiraKey || '',
    }));
  }, [filteredPpmProjects]);

  const panelRows = useMemo(() => {
    if (!topPanel) return [];
    if (topPanel === 'budget') return panelInternalRows.slice().sort((a, b) => (b.budgetedCost || 0) - (a.budgetedCost || 0));
    // total/internal: just show internal projects list (this page already has the Jira section below)
    return panelInternalRows.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [panelInternalRows, topPanel]);

  const isLoading = (loading && projects.length === 0) && (loadingPpm && ppmProjects.length === 0);

  // If a project is selected, show the Jira views
  if (selectedProject) {
    return (
      <PageLoader loading={isLoading}>
        <Container maxWidth="xl" sx={{ pt: 1, pb: 4 }}>
        <div className="project-detail-view">
          <div className="project-detail-header">
            <button
              className="back-button"
              onClick={() => {
                setSelectedProject(null);
                setProjectDetails(null);
                setBoards([]);
                setSelectedBoard(null);
                setBoardIssues([]);
                setIssues([]);
                setViewMode('summary');
                navigate('/projects');
              }}
            >
              ← Back to Projects
            </button>
            {loadingDetails ? (
              <div className="loading-container">
                <CircularProgress size={20} />
              </div>
            ) : projectDetails ? (
              <div className="project-header-info">
                {projectDetails.avatarUrls?.['48x48'] && (
                  <img src={projectDetails.avatarUrls['48x48']} alt={projectDetails.name} className="project-header-avatar" />
                )}
                <div>
                  <h2>{projectDetails.name}</h2>
                  <div className="project-header-meta">
                    <span className="project-key-badge">{projectDetails.key}</span>
                    {projectDetails.lead && (
                      <span className="project-lead">Lead: {projectDetails.lead.displayName}</span>
                    )}
                    {projectDetails.url && (
                      <button
                        className="btn-tertiary btn-small"
                        onClick={() => openInJira(`/browse/${projectDetails.key}`)}
                      >
                        Open in Jira
                      </button>
                    )}
                  </div>
                  {projectDetails.description && (
                    <p className="project-description">{projectDetails.description}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="project-header-info">
                <h2>{selectedProject.name}</h2>
                <span className="project-key-badge">{selectedProject.key}</span>
              </div>
            )}
          </div>

          <div className="view-tabs">
            <button
              className={`view-tab ${viewMode === 'summary' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('summary');
                if (issues.length === 0) {
                  fetchProjectIssues(selectedProject.key);
                }
              }}
            >
              Summary
            </button>
            <button
              className={`view-tab ${viewMode === 'board' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('board');
                if (issues.length === 0) {
                  fetchProjectIssues(selectedProject.key);
                }
              }}
            >
              Board {boards.length > 0 && `(${boards.length})`}
            </button>
            <button
              className={`view-tab ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('list');
                if (issues.length === 0) {
                  fetchProjectIssues(selectedProject.key);
                }
              }}
            >
              List {issues.length > 0 && `(${issues.length})`}
            </button>
            <button
              className={`view-tab ${viewMode === 'timeline' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('timeline');
                if (issues.length === 0) {
                  fetchProjectIssues(selectedProject.key);
                }
              }}
            >
              Timeline {issues.length > 0 && `(${issues.length})`}
            </button>
          </div>

          {viewMode === 'summary' && (
            <div className="view-content">
              <div className="summary-stats">
                <div className="stat-box">
                  <div className="stat-value">{issues.length}</div>
                  <div className="stat-label">Total Work Items</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{boards.length}</div>
                  <div className="stat-label">Boards</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">
                    {issues.filter(i => i.fields.status.name.toLowerCase().includes('progress')).length}
                  </div>
                  <div className="stat-label">In Progress</div>
                </div>
              </div>

              {boards.length > 0 && (
                <div className="boards-section">
                  <h4>Boards</h4>
                  <div className="boards-grid">
                    {boards.map((board) => (
                      <div key={board.id} className="board-card" onClick={() => openInJira(`/jira/software/projects/${selectedProject.key}/boards/${board.id}`)}>
                        <div className="board-name">{board.name}</div>
                        <div className="board-type">{board.type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {viewMode === 'board' && (
            <div className="view-content board-view-content">
            {boards.length > 0 ? (
              <>
                {(boards.length > 1 || selectedBoard) && (
                  <div className="board-selector">
                    {boards.length > 1 ? (
                      <>
                        <label>Select Board:</label>
                        <select
                          className="board-select"
                          value={selectedBoard?.id || ''}
                          onChange={(e) => {
                            const board = boards.find(b => b.id === parseInt(e.target.value));
                            if (board) {
                              setSelectedBoard(board);
                              fetchBoardIssues(board.id);
                            }
                          }}
                        >
                          {boards.map((board) => (
                            <option key={board.id} value={board.id}>
                              {board.name} ({board.type})
                            </option>
                          ))}
                        </select>
                      </>
                    ) : selectedBoard && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <label>Board:</label>
                        <span style={{ fontWeight: 600, color: '#1f2937' }}>{selectedBoard.name}</span>
                      </div>
                    )}
                    {selectedBoard && (
                      <button
                        className="btn-tertiary btn-small"
                        onClick={() => openInJira(`/jira/software/projects/${selectedProject.key}/boards/${selectedBoard.id}`)}
                      >
                        Open in Jira
                      </button>
                    )}
                  </div>
                )}
                {loadingBoard ? (
                  <div className="loading-container">
                    <CircularProgress size={20} />
                    <p>Loading board work items...</p>
                  </div>
                ) : (
                  <div className="kanban-board">
                    {getKanbanColumns().map((column) => (
                      <div key={column.id} className="kanban-column">
                        <div className="kanban-column-header">
                          <h4>{column.title}</h4>
                          <span className="column-count">({column.issues.length})</span>
                        </div>
                        <div className="kanban-column-content">
                          {column.issues.length > 0 ? (
                            column.issues.map((issue) => (
                              <div
                                key={issue.id}
                                className="kanban-card"
                                onClick={() => openInJira(`/browse/${issue.key}`)}
                              >
                                <div className="kanban-card-header">
                                  <div className="issue-key-small">{issue.key}</div>
                                  <span
                                    className="issue-status-badge"
                                    style={{ backgroundColor: getStatusColor(issue.fields.status.name) }}
                                  >
                                    {issue.fields.status.name}
                                  </span>
                                </div>
                                <div className="kanban-card-title">{issue.fields.summary}</div>
                                <div className="kanban-card-footer">
                                  {issue.fields.assignee && (
                                    <div className="assignee-info">
                                      <span className="assignee-icon">👤</span>
                                      <span>{issue.fields.assignee.displayName}</span>
                                    </div>
                                  )}
                                  {issue.fields.duedate && (
                                    <div className="kanban-card-due-date">
                                      {(() => {
                                        const dueDate = new Date(issue.fields.duedate);
                                        const isOverdue = dueDate < new Date();
                                        return (
                                          <span style={{ 
                                            color: isOverdue ? '#EF4444' : '#F59E0B',
                                            fontWeight: '600',
                                            fontSize: '11px'
                                          }}>
                                            📅 {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="empty-column">
                              <p>No work items</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <p>No boards found for this project.</p>
                <button className="btn-primary" onClick={() => fetchProjectIssues(selectedProject.key)}>
                  Load Work Items
                </button>
              </div>
            )}
          </div>
          )}

          {viewMode === 'list' && (
            <div className="view-content list-view-content">
              {issues.length > 0 ? (
                <div style={{ width: '100%', overflowX: 'auto' }}>
                  <table className="issues-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: '120px', width: '120px' }}>Key</th>
                        <th>Summary</th>
                        <th style={{ minWidth: '140px', width: '140px' }}>Status</th>
                        <th style={{ minWidth: '150px' }}>Assignee</th>
                        <th style={{ minWidth: '100px' }}>Type</th>
                        <th style={{ minWidth: '100px' }}>Priority</th>
                        <th style={{ minWidth: '120px' }}>Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issues.map((issue) => (
                        <tr key={issue.id} onClick={() => openInJira(`/browse/${issue.key}`)}>
                          <td><strong style={{ color: '#0052cc' }}>{issue.key}</strong></td>
                          <td style={{ fontWeight: 500 }}>{issue.fields.summary}</td>
                          <td>
                            <span
                              className="issue-status-badge"
                              style={{ backgroundColor: getStatusColor(issue.fields.status.name) }}
                            >
                              {issue.fields.status.name}
                            </span>
                          </td>
                          <td>{issue.fields.assignee?.displayName || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Unassigned</span>}</td>
                          <td>{issue.fields.issuetype?.name || 'Task'}</td>
                          <td>{issue.fields.priority?.name || <span style={{ color: '#9CA3AF' }}>None</span>}</td>
                          <td>
                            {issue.fields.duedate ? (
                              (() => {
                                const dueDate = new Date(issue.fields.duedate);
                                const isOverdue = dueDate < new Date();
                                return (
                                  <span style={{ 
                                    color: isOverdue ? '#EF4444' : '#F59E0B',
                                    fontWeight: isOverdue ? '600' : '500'
                                  }}>
                                    {dueDate.toLocaleDateString()}
                                  </span>
                                );
                              })()
                            ) : (
                              <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No due date</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No work items found.</p>
                  <button className="btn-primary" onClick={() => fetchProjectIssues(selectedProject.key)}>
                    Load Work Items
                  </button>
                </div>
              )}
            </div>
          )}

          {viewMode === 'timeline' && (
            <div className="view-content timeline-view-content">
              {issues.length > 0 ? (
                <div className="timeline-horizontal-container">
                  <div className="timeline-track">
                    {issues
                      .sort((a, b) => {
                        const dateA = a.fields.created ? new Date(a.fields.created).getTime() : 0;
                        const dateB = b.fields.created ? new Date(b.fields.created).getTime() : 0;
                        return dateA - dateB;
                      })
                      .map((issue, index) => (
                        <div key={issue.id} className="timeline-item-horizontal">
                          <div className="timeline-card" onClick={() => openInJira(`/browse/${issue.key}`)}>
                            <div className="timeline-card-header">
                              <span className="issue-key-small">{issue.key}</span>
                              <span
                                className="issue-status-badge"
                                style={{ backgroundColor: getStatusColor(issue.fields.status.name) }}
                              >
                                {issue.fields.status.name}
                              </span>
                            </div>
                            <h4 className="timeline-card-title">{issue.fields.summary}</h4>
                            <div className="timeline-card-meta">
                              {issue.fields.assignee && (
                                <span className="timeline-meta-item">👤 {issue.fields.assignee.displayName}</span>
                              )}
                              {issue.fields.issuetype && (
                                <span className="timeline-meta-item">📋 {issue.fields.issuetype.name}</span>
                              )}
                              {issue.fields.priority && (
                                <span className="timeline-meta-item">⚡ {issue.fields.priority.name}</span>
                              )}
                            </div>
                            <div className="timeline-card-footer">
                              {issue.fields.duedate ? (
                                (() => {
                                  const dueDate = new Date(issue.fields.duedate);
                                  const isOverdue = dueDate < new Date();
                                  return (
                                    <div className="timeline-card-date" style={{ color: isOverdue ? '#EF4444' : '#F59E0B', fontWeight: '600' }}>
                                      📅 Due {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  );
                                })()
                              ) : (
                                <div className="timeline-card-date">
                                  📅 {issue.fields.created ? new Date(issue.fields.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                                </div>
                              )}
                            </div>
                          </div>
                          {index < issues.length - 1 && <div className="timeline-connector" />}
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No work items found.</p>
                  <button className="btn-primary" onClick={() => fetchProjectIssues(selectedProject.key)}>
                    Load Work Items
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </Container>
      </PageLoader>
    );
  }

  // Main projects list view
  return (
    <PageLoader loading={isLoading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 4 }}>
      <PageHeader
        title="Projects"
        subtitle="Internal delivery + Jira programs in one view."
        chips={[
          { label: `${ppmProjects.length + projects.length} total` },
          { label: `${ppmProjects.length} internal` },
          { label: `${projects.length} Jira` },
        ]}
        actions={
          <>
            <TextField
              size="small"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: { xs: '100%', sm: 360 },
              }}
            />
            {canEdit ? (
              <Button
                variant="contained"
                onClick={() => setCreateInternalOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                New project
              </Button>
            ) : null}
          </>
        }
      />

      <Snackbar
        open={showToast}
        autoHideDuration={3000}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toastMessage.variant === 'critical' ? 'error' : 'success'} variant="filled">
          {toastMessage.label}
        </Alert>
      </Snackbar>

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Box onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'total' ? null : 'total')); }} sx={{ cursor: 'pointer' }}>
            <StatCard title="Total projects" value={summary.total} subtitle="Internal + Jira" accent={tokens.color.accent} />
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'internal' ? null : 'internal')); }} sx={{ cursor: 'pointer' }}>
            <StatCard title="Internal" value={summary.internalTotal} subtitle={`${summary.internalAvgProgress.toFixed(0)}% avg progress`} accent={tokens.color.success} />
          </Box>
        </Grid>
        {canEdit ? (
          <>
            <Grid item xs={12} md={3}>
              <Box onClick={() => { setShowAllPanel(false); setTopPanel((p) => (p === 'budget' ? null : 'budget')); }} sx={{ cursor: 'pointer' }}>
                <StatCard title="Allocated budget" value={moneyCompact.format(summary.internalBudget)} subtitle="Budgeted cost rollup" accent={tokens.color.info} />
              </Box>
            </Grid>
            {/* Actuals are tracked per project, but rollups use allocated budgetedCost */}
          </>
        ) : null}
      </Grid>

      <Collapse in={!!topPanel} unmountOnExit>
        <Card variant="outlined" sx={{ borderColor: tokens.color.border, mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                  {topPanel === 'budget'
                    ? 'Internal projects (highest allocated budget)'
                    : topPanel === 'internal'
                      ? 'Internal projects'
                      : 'Projects summary'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click any internal project below to open it.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)}>
                  {showAllPanel ? 'Show less' : 'Show all'}
                </Button>
                <Button size="small" variant="outlined" onClick={() => setTopPanel(null)}>
                  Close
                </Button>
              </Stack>
            </Box>
            <Divider sx={{ my: 1.25 }} />
            {panelRows.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No internal projects match your current filters.
              </Alert>
            ) : showAllPanel ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {panelRows.slice(0, 1000).map((r: any) => (
                  <Box
                    key={r.id}
                    sx={{ p: 1, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                    onClick={() => navigate(`/projects/internal/${r.id}`, { state: { from: location.pathname } })}
                  >
                    <Typography sx={{ fontWeight: 650 }} noWrap>
                      {r.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {r.portfolio}
                      {canEdit ? ` • ${moneyCompact.format(r.budgetedCost)} allocated budget` : ''}
                      {` • ${Math.round(r.percentComplete)}%`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {panelRows.slice(0, 5).map((r: any) => (
                  <Box
                    key={r.id}
                    sx={{ p: 1, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                    onClick={() => navigate(`/projects/internal/${r.id}`, { state: { from: location.pathname } })}
                  >
                    <Typography sx={{ fontWeight: 650 }} noWrap>
                      {r.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {r.portfolio}
                      {canEdit ? ` • ${moneyCompact.format(r.budgetedCost)} allocated budget` : ''}
                      {` • ${Math.round(r.percentComplete)}%`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              overflow: 'hidden',
              borderColor: tokens.color.border,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                    Internal projects
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Product work + initiatives managed in Nexus
                  </Typography>
                </Box>
                {loadingPpm ? <Typography variant="caption" color="text.secondary">Loading…</Typography> : null}
              </Box>
              <Divider sx={{ my: 1.25 }} />
              {filteredPpmProjects.length === 0 && !loadingPpm ? (
                <Alert severity="info" variant="outlined">
                  No internal projects match your search.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {ppmProjectsByPortfolio.map((group) => (
                    <Box key={group.portfolioName}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          userSelect: 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setOpenPortfolioGroups((p) => ({
                                ...p,
                                [group.portfolioName]: !(p[group.portfolioName] ?? false),
                              }))
                            }
                            sx={{
                              border: `1px solid ${tokens.color.border}`,
                              borderRadius: `${tokens.radius.md}px`,
                              width: 30,
                              height: 30,
                            }}
                            aria-label={`Toggle ${group.portfolioName}`}
                          >
                            <ExpandMoreIcon
                              sx={{
                                transition: 'transform 160ms ease',
                                transform: (openPortfolioGroups[group.portfolioName] ?? false) ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}
                            />
                          </IconButton>
                          <Typography sx={{ fontWeight: 700 }} noWrap title={group.portfolioName}>
                            {group.portfolioName}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {group.projects.length} project{group.projects.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      <Divider sx={{ mt: 0.75, mb: 1.25 }} />
                      <Collapse in={openPortfolioGroups[group.portfolioName] ?? false} timeout={180} unmountOnExit>
                        <Grid container spacing={1.5}>
                          {group.projects.map((p) => (
                            <Grid key={p.id} item xs={12} sm={6} md={4} lg={3}>
                              <Card
                                variant="outlined"
                                sx={{
                                  height: '100%',
                                  borderColor: tokens.color.border,
                                  position: 'relative',
                                  background: '#fff',
                                  transition: 'border-color 120ms ease, background-color 120ms ease',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: 3,
                                    background: tokens.color.accent,
                                  },
                                  '&:hover': {
                                    borderColor: tokens.color.borderStrong,
                                    backgroundColor: tokens.color.surfaceMuted,
                                  },
                                }}
                              >
                                <CardActionArea onClick={() => navigate(`/projects/internal/${p.id}`, { state: { from: location.pathname } })} sx={{ height: '100%' }}>
                                  <CardContent>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                      <Avatar
                                        sx={{
                                          width: 34,
                                          height: 34,
                                          borderRadius: 2,
                                          bgcolor: tokens.color.accentSoft,
                                          color: tokens.color.accentHover,
                                          fontWeight: 700,
                                        }}
                                      >
                                        P
                                      </Avatar>
                                      <Typography sx={{ fontWeight: 650, flex: 1, minWidth: 0 }} noWrap>
                                        {p.name}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`PPM ${p.id.slice(0, 6)}`}
                                        sx={{ height: 22, fontWeight: 650, flexShrink: 0 }}
                                      />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                      Updated {String(p.updatedAt).slice(0, 10)}
                                    </Typography>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              overflow: 'hidden',
              borderColor: tokens.color.border,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                    Jira projects
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Imported programs + workstreams
                  </Typography>
                </Box>
                {loading ? <Typography variant="caption" color="text.secondary">Loading…</Typography> : null}
              </Box>
              <Divider sx={{ my: 1.25 }} />
              {filteredProjects.length === 0 && !loading ? (
                <Alert severity="info" variant="outlined">
                  No Jira projects match your search.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {jiraProjectsByPortfolio.map((group) => (
                    <Box key={group.portfolioName}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, userSelect: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setOpenJiraPortfolioGroups((p) => ({
                                ...p,
                                [group.portfolioName]: !(p[group.portfolioName] ?? false),
                              }))
                            }
                            sx={{
                              border: `1px solid ${tokens.color.border}`,
                              borderRadius: `${tokens.radius.md}px`,
                              width: 30,
                              height: 30,
                            }}
                            aria-label={`Toggle ${group.portfolioName}`}
                          >
                            <ExpandMoreIcon
                              sx={{
                                transition: 'transform 160ms ease',
                                transform: (openJiraPortfolioGroups[group.portfolioName] ?? false) ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}
                            />
                          </IconButton>
                          <Typography sx={{ fontWeight: 700 }} noWrap title={group.portfolioName}>
                            {group.portfolioName}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {group.projects.length} project{group.projects.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      {group.portfolioName === 'No portfolio yet' ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          These Jira projects aren’t attached to a portfolio yet. {canEdit ? 'Use the Portfolio pill on a project card to assign one.' : ''}
                        </Typography>
                      ) : null}
                      <Divider sx={{ mt: 0.75, mb: 1.25 }} />
                      <Collapse in={openJiraPortfolioGroups[group.portfolioName] ?? false} timeout={180} unmountOnExit>
                        <Grid container spacing={1.5}>
                          {group.projects.map((project) => {
                            const summary = projectSummaries.get(project.key);
                            const jiraPortfolio = jiraPortfolioByKey[project.key];
                            return (
                              <Grid key={project.id} item xs={12} sm={6} md={4} lg={3}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderColor: tokens.color.border,
                                    position: 'relative',
                                    background: '#fff',
                                    transition: 'border-color 120ms ease, background-color 120ms ease',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 3,
                                      background: tokens.color.info,
                                    },
                                    '&:hover': {
                                      borderColor: tokens.color.borderStrong,
                                      backgroundColor: tokens.color.surfaceMuted,
                                    },
                                  }}
                                >
                                  <CardActionArea onClick={() => navigate(`/projects/${project.key}`)} sx={{ height: '100%' }}>
                                    <CardContent>
                                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 0 }}>
                                          <Avatar
                                            src={project.avatarUrls?.['48x48'] || undefined}
                                            sx={{
                                              width: 34,
                                              height: 34,
                                              borderRadius: 2,
                                              bgcolor: tokens.color.accentSoft,
                                              color: tokens.color.accentHover,
                                              fontWeight: 700,
                                            }}
                                          >
                                            J
                                          </Avatar>
                                          <Typography sx={{ fontWeight: 650, flex: 1, minWidth: 0 }} noWrap>
                                            {project.name}
                                          </Typography>
                                        </Box>
                                        <Chip size="small" label={project.key} sx={{ height: 22, fontWeight: 650, background: tokens.color.accentSoft, color: tokens.color.accentHover }} />
                                      </Stack>

                                      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box
                                          onClick={(e) => (canEdit ? openPortfolioMenu(e, project.key, project.name) : undefined)}
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: `${tokens.radius.pill}px`,
                                            border: `1px solid ${tokens.color.border}`,
                                            background: '#fff',
                                            cursor: canEdit ? 'pointer' : 'default',
                                            maxWidth: '100%',
                                          }}
                                        >
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            Portfolio:
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 650, minWidth: 0 }} noWrap>
                                            {jiraPortfolio?.portfolioName || 'None'}
                                          </Typography>
                                          {canEdit ? <ArrowDropDownIcon sx={{ fontSize: 18, color: tokens.color.textMuted }} /> : null}
                                        </Box>
                                      </Box>

                                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                                        <Chip size="small" variant="outlined" label={`${summary?.totalIssues ?? '—'} items`} sx={{ height: 22 }} />
                                        <Chip size="small" variant="outlined" label={`${summary?.openIssues ?? '—'} open`} sx={{ height: 22 }} />
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={
                                            summary
                                              ? `${Math.round(Number(summary.percentComplete || 0))}% complete`
                                              : '— complete'
                                          }
                                          sx={{ height: 22, fontWeight: 650 }}
                                        />
                                      </Stack>
                                    </CardContent>
                                  </CardActionArea>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Menu
        anchorEl={portfolioMenuAnchor}
        open={!!portfolioMenuAnchor && !!portfolioMenuProject}
        onClose={closePortfolioMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={async () => {
            if (!portfolioMenuProject) return;
            await setJiraPortfolio(portfolioMenuProject.jiraKey, portfolioMenuProject.name, '');
            closePortfolioMenu();
          }}
        >
          <em>None</em>
        </MenuItem>
        {portfolios.map((p) => (
          <MenuItem
            key={p.id}
            onClick={async () => {
              if (!portfolioMenuProject) return;
              await setJiraPortfolio(portfolioMenuProject.jiraKey, portfolioMenuProject.name, p.id);
              closePortfolioMenu();
            }}
          >
            {p.name}
          </MenuItem>
        ))}
      </Menu>

      {canEdit ? (
      <>
      <Dialog open={createInternalOpen} onClose={() => setCreateInternalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New internal project</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField label="Name" value={createInternal.name} onChange={(e) => setCreateInternal((v) => ({ ...v, name: e.target.value }))} fullWidth />
          <TextField label="Description (optional)" value={createInternal.description} onChange={(e) => setCreateInternal((v) => ({ ...v, description: e.target.value }))} fullWidth multiline minRows={3} />
          <TextField select label="Portfolio" value={createInternal.portfolioId} onChange={(e) => setCreateInternal((v) => ({ ...v, portfolioId: e.target.value }))} fullWidth>
            <MenuItem value="">
              <em>Select portfolio</em>
            </MenuItem>
            {portfolios.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Allocated Budget"
            value={createInternal.budgetedCost}
            onChange={(e) => setCreateInternal((v) => ({ ...v, budgetedCost: e.target.value }))}
            fullWidth
            required
            helperText={(() => {
              const p = portfolios.find((x) => String(x.id) === String(createInternal.portfolioId));
              const portfolioBudget = Number((p as any)?.totalBudget || 0);
              const allocated = (ppmProjects || []).filter((pr: any) => String(pr.portfolioId || '') === String(createInternal.portfolioId)).reduce((s: number, pr: any) => s + Number(pr?.budgetedCost || 0), 0);
              const remaining = portfolioBudget - allocated;
              if (!createInternal.portfolioId) return 'Pick a portfolio to see remaining budget.';
              if (!(portfolioBudget > 0)) return 'This portfolio has no budget set yet.';
              return `Portfolio remaining budget: ${moneyCompact.format(Math.max(0, remaining))} (allocated ${moneyCompact.format(allocated)} of ${moneyCompact.format(portfolioBudget)})`;
            })()}
          />
          <TextField
            select
            label="Lead"
            value={createInternal.leadUserId}
            onChange={(e) => setCreateInternal((v) => ({ ...v, leadUserId: e.target.value }))}
            fullWidth
            helperText="Optional (used for members + rollups)."
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {allUsers.map((u: any) => (
              <MenuItem key={u.id} value={u.id}>
                {u.displayName || u.username || u.email || u.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Manager"
            value={createInternal.managerUserId}
            onChange={(e) => setCreateInternal((v) => ({ ...v, managerUserId: e.target.value }))}
            fullWidth
            helperText="Optional."
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {allUsers.map((u: any) => (
              <MenuItem key={u.id} value={u.id}>
                {u.displayName || u.username || u.email || u.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Start date"
            type="date"
            value={createInternal.startDate}
            onChange={(e) => setCreateInternal((v) => ({ ...v, startDate: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Planned end date"
            type="date"
            value={createInternal.plannedEndDate}
            onChange={(e) => setCreateInternal((v) => ({ ...v, plannedEndDate: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCreateInternalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={(() => {
              if (!createInternal.name.trim() || !createInternal.portfolioId || !(Number(createInternal.budgetedCost || 0) > 0)) return true;
              const p = portfolios.find((x) => String(x.id) === String(createInternal.portfolioId));
              const portfolioBudget = Number((p as any)?.totalBudget || 0);
              if (!(portfolioBudget > 0)) return false; // backend will reject if portfolio has no budget; allow click to surface error
              const allocated = (ppmProjects || [])
                .filter((pr: any) => String(pr.portfolioId || '') === String(createInternal.portfolioId))
                .reduce((s: number, pr: any) => s + Number(pr?.budgetedCost || 0), 0);
              const remaining = portfolioBudget - allocated;
              return Number(createInternal.budgetedCost || 0) > remaining + 1e-9;
            })()}
            onClick={createInternalProject}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={attachJiraOpen} onClose={() => setAttachJiraOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Jira project to portfolio</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
          <TextField label="Jira Key" value={attachJira.jiraKey} fullWidth disabled />
          <TextField label="Name" value={attachJira.name} fullWidth disabled />
          <TextField select label="Portfolio" value={attachJira.portfolioId} onChange={(e) => setAttachJira((v) => ({ ...v, portfolioId: e.target.value }))} fullWidth>
            <MenuItem value="">
              <em>Select portfolio</em>
            </MenuItem>
            {portfolios.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setAttachJiraOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!attachJira.portfolioId} onClick={attachJiraProjectToPortfolio}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
      </>
      ) : null}
      </Container>
    </PageLoader>
  );
};

export default ProjectList;
